import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import API from "@/lib/api";
import {
  cacheResponse,
  getCachedResponse,
  shouldCache,
  queueOfflineWrite,
  getPendingWrites,
  clearPendingWrite,
} from "@/lib/offline-store";

export function useOfflineSync() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const syncingRef = useRef(false);

  // Track online/offline status
  useEffect(() => {
    const onOnline = () => {
      setIsOnline(true);
      toast.success("Back online", { description: "Syncing pending changes..." });
      syncPendingWrites();
    };
    const onOffline = () => {
      setIsOnline(false);
      toast.warning("You are offline", {
        description: "Changes will be saved locally and synced when you reconnect.",
        duration: 5000,
      });
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  // Check pending writes count
  useEffect(() => {
    const checkPending = async () => {
      const writes = await getPendingWrites();
      setPendingCount(writes.length);
    };
    checkPending();
    const interval = setInterval(checkPending, 10000);
    return () => clearInterval(interval);
  }, []);

  // Sync pending writes when back online
  const syncPendingWrites = useCallback(async () => {
    if (syncingRef.current || !navigator.onLine) return;
    syncingRef.current = true;
    setSyncing(true);

    try {
      const writes = await getPendingWrites();
      if (writes.length === 0) {
        setSyncing(false);
        syncingRef.current = false;
        return;
      }

      let synced = 0;
      let failed = 0;

      for (const write of writes) {
        try {
          if (write.method === "PUT") {
            await API.put(write.path, write.data);
          } else if (write.method === "POST") {
            await API.post(write.path, write.data);
          } else if (write.method === "DELETE") {
            await API.delete(write.path);
          }
          await clearPendingWrite(write.id);
          synced++;
        } catch (err) {
          // If it's a 4xx error, the write is invalid — remove it
          if (err.response && err.response.status >= 400 && err.response.status < 500) {
            await clearPendingWrite(write.id);
            failed++;
          }
          // 5xx errors: keep in queue for retry
        }
      }

      if (synced > 0) {
        toast.success(`Synced ${synced} pending change${synced > 1 ? "s" : ""}`);
      }
      if (failed > 0) {
        toast.error(`${failed} change${failed > 1 ? "s" : ""} could not be synced`);
      }

      const remaining = await getPendingWrites();
      setPendingCount(remaining.length);
    } finally {
      setSyncing(false);
      syncingRef.current = false;
    }
  }, []);

  return { isOnline, pendingCount, syncing, syncPendingWrites };
}

// ─── Axios Interceptors for Auto-Caching ───
// Call this once at app startup to wire caching into all API calls

let interceptorsInstalled = false;

export function installOfflineInterceptors() {
  if (interceptorsInstalled) return;
  interceptorsInstalled = true;

  // Response interceptor: cache successful GET responses
  API.interceptors.response.use(
    (response) => {
      const path = response.config.url;
      if (response.config.method === "get" && path && shouldCache(path)) {
        cacheResponse(path, response.data).catch(() => {});
      }
      return response;
    },
    async (error) => {
      // If network error on GET, try offline cache
      if (
        error.config &&
        error.config.method === "get" &&
        (!error.response || error.response.status === 0 || !navigator.onLine)
      ) {
        const path = error.config.url;
        if (path && shouldCache(path)) {
          const cached = await getCachedResponse(path);
          if (cached) {
            console.log(`[Offline] Serving cached: ${path}`);
            return { data: cached, status: 200, config: error.config, _fromCache: true };
          }
        }
      }

      // If network error on write (POST/PUT/DELETE), queue for later sync
      // SKIP auth endpoints — login/logout must never be queued offline
      if (
        error.config &&
        ["post", "put", "delete"].includes(error.config.method) &&
        (!error.response || error.response.status === 0 || !navigator.onLine) &&
        !error.config.url?.includes("/auth/")
      ) {
        const method = error.config.method.toUpperCase();
        const path = error.config.url;
        const data = error.config.data ? JSON.parse(error.config.data) : null;
        const queued = await queueOfflineWrite(method, path, data);
        if (queued) {
          toast.info("Saved offline", {
            description: "This change will sync when you're back online.",
          });
          // Return a mock success so the UI doesn't break
          return {
            data: { _offline: true, _queued: true },
            status: 200,
            config: error.config,
          };
        }
      }

      return Promise.reject(error);
    }
  );
}
